import React from 'react';
import { useNavigate } from 'react-router-dom';
import SocialFooter from '../components/SocialFooter';
import { ArrowLeft, ShieldCheck, Heartbeat, Database, Bell, MapPin, Camera } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cardio-journey-app/artifacts/tyj7q2ct_logo%20365.png";

const PERMISSIONS = [
  {
    icon: Heartbeat,
    title: 'Health & Fitness Data',
    description: 'We collect workout duration, calories, distance, and weight entries that you manually log.',
    why: 'To track your 365-day cardio journey, calculate streaks, and generate personalized insights.',
    required: true,
  },
  {
    icon: Camera,
    title: 'Camera & Photos',
    description: 'Access to your camera and photo library for Before & After progress photos.',
    why: 'To let you upload transformation photos and compare them side by side over time.',
    required: false,
  },
  {
    icon: Bell,
    title: 'Notifications',
    description: 'Permission to send push notifications to your device.',
    why: 'To send daily workout reminders, streak alerts, and weekly progress summaries. You can turn these off anytime.',
    required: false,
  },
  {
    icon: MapPin,
    title: 'Location',
    description: 'Track365 does NOT request or collect your location data.',
    why: 'We respect your privacy. No GPS or location tracking is used.',
    required: false,
    notUsed: true,
  },
];

const AppPermissions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background relative overflow-hidden" data-testid="permissions-screen">
      <div className="absolute inset-0 opacity-[0.08] pointer-events-none" style={{ backgroundImage: `url(${LOGO_URL})`, backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundSize: '50%' }} />

      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-xl border-b border-border px-6 py-4">
        <div className="max-w-2xl mx-auto flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-card flex items-center justify-center" data-testid="back-btn">
            <ArrowLeft weight="bold" className="w-5 h-5 text-foreground" />
          </button>
          <img src={LOGO_URL} alt="Track365" className="w-40 h-auto" />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-6 py-8 relative z-10">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mx-auto mb-4">
            <ShieldCheck weight="duotone" className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            App Permissions & Data
          </h1>
          <p className="text-muted-foreground">
            Your privacy matters. Here's exactly what Track365 accesses and why.
          </p>
        </div>

        {/* Privacy commitment */}
        <div className="premium-card p-5 mb-6 border-[#D4AF37]/20">
          <p className="text-sm text-foreground leading-relaxed">
            Track365 Cardio is built with <span className="text-[#D4AF37] font-semibold">privacy-first principles</span>. We only collect data that directly improves your fitness tracking experience. We never sell your data to third parties.
          </p>
        </div>

        {/* Permissions List */}
        <div className="space-y-4 mb-8">
          {PERMISSIONS.map((perm, idx) => (
            <div key={idx} className={`premium-card p-5 ${perm.notUsed ? 'opacity-60' : ''}`} data-testid={`permission-${idx}`}>
              <div className="flex items-start gap-4">
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${perm.notUsed ? 'bg-muted/10' : 'bg-[#D4AF37]/10'}`}>
                  <perm.icon weight="duotone" className={`w-6 h-6 ${perm.notUsed ? 'text-muted-foreground' : 'text-[#D4AF37]'}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-foreground font-semibold">{perm.title}</h3>
                    {perm.required && (
                      <span className="text-[9px] bg-[#D4AF37]/10 text-[#D4AF37] px-2 py-0.5 rounded-full font-bold uppercase">Required</span>
                    )}
                    {perm.notUsed && (
                      <span className="text-[9px] bg-green-500/10 text-green-500 px-2 py-0.5 rounded-full font-bold uppercase">Not Used</span>
                    )}
                    {!perm.required && !perm.notUsed && (
                      <span className="text-[9px] bg-card text-muted-foreground px-2 py-0.5 rounded-full font-bold uppercase border border-border">Optional</span>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{perm.description}</p>
                  <p className="text-xs text-[#D4AF37]/80">
                    <span className="font-semibold">Why: </span>{perm.why}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Data Storage */}
        <div className="premium-card p-5 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
              <Database weight="duotone" className="w-6 h-6 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="text-foreground font-semibold mb-2">How We Store Your Data</h3>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li>- All data is encrypted in transit and at rest</li>
                <li>- Stored on secure cloud servers</li>
                <li>- You can export or delete your data at any time</li>
                <li>- We retain data only as long as your account is active</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="text-center text-sm text-muted-foreground mb-6">
          <p>For more details, see our{' '}
            <a href="/privacy" className="text-[#D4AF37] hover:underline">Privacy Policy</a>.
          </p>
        </div>

        <SocialFooter />
      </main>
    </div>
  );
};

export default AppPermissions;
